import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { styled } from '@compiled/react';
import type { LogOutAction } from '@helpCenter/epics/logout';
import { isAdvancedHelpCenter } from '@helpCenter/util/advanced-help-center';
import { ThemedBadge as AvatarBadge } from '@helpCenter/view/common/badges';
import { ForgeActionModule } from '@helpCenter/view/forge-ui/forge-action';
import {
    isWrmOptimizationsEnabled,
    isUnifiedHelpReportingPageEnabled,
    isAdvancedCustomizationEnabled,
} from 'feature-flags';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import type { Column } from 'rest/requests-column-settings';
import * as grid from 'view/styles/grid';
import { sizes } from 'view/styles/sizes-viewport';
import { AnalyticsContext, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Profile } from '@atlaskit/atlassian-navigation';
import Avatar from '@atlaskit/avatar';
import { CustomItem, HeadingItem, MenuGroup, Section } from '@atlaskit/menu';
import Popup from '@atlaskit/popup';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { colors, layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { Button, Link } from '@atlassian/help-center-common-component/analytics';
import { HtmlAnchor } from '@atlassian/help-center-common-component/analytics/html-anchor';
import { initializeConnect } from '@atlassian/help-center-common-component/connect-fragment';
import { HELP_CENTERS_MANAGEMENT_BASE_PATH } from '@atlassian/help-center-common-component/constants';
import {
    USER_MENU_ACTION_COMPONENT,
    USER_MENU_ACTION_SECTION_MODULE,
} from '@atlassian/help-center-common-component/forge-ui/constants';
import { ForgeModalContext } from '@atlassian/help-center-common-component/forge-ui/modal-context';
import { ForgeModalContextProvider } from '@atlassian/help-center-common-component/forge-ui/modal-context-provider';
import type { SetExtensionToDisplayType } from '@atlassian/help-center-common-component/forge-ui/types';
import { getRelativeBasePath } from '@atlassian/help-center-common-util/history';
import { withDeferredWrmLoaded } from '@atlassian/help-center-common-util/wrm';
import { AccountDetails } from '../account-details';
import MultiHcDropdown from '../multi-hc-dropdown';
import messages from './messages';

export interface CustomMenuLinkProps {
    id: string | undefined;
    key: string | undefined;
    label: string | undefined;
    href: string;
    styleClass: string | undefined;
}

interface Portal {
    id?: number;
    name?: string;
    isProjectSimplified: boolean;
    isProjectAdmin: boolean;
    canEditAnnouncement: boolean;
}
export interface StateProps {
    avatarSrc: string | undefined;
    displayName: string;
    email: string;
    customMenuLinks: CustomMenuLinkProps[];
    routeName?: string;
    portal?: Portal;
    pendingApprovalCount?: number;
    approvalCount?: number;
    canAdministerJIRA: boolean;
    canCustomiseHelpCenter?: boolean;
    myRequestCount?: number;
    defaultColumnSettings?: Column[];
    jsmAIConfigEnabled?: boolean;
    isOpen?: boolean;
}

export interface DispatchProps {
    onLogoutClick: () => LogOutAction;
    onOpenAndClose?: (e: { isOpen: boolean }) => void;
    openHelpCenterCustomizationSidebar: () => void;
    openRequestListCustomizationSidebar: () => void;
}

export type Props = StateProps & DispatchProps;

interface ForgeTitleProps {
    onClose: (actionSubjectId?: string) => void;
}
export const ForgeTitleMenuLinks = ({ onClose }: ForgeTitleProps) => {
    const {
        data: { extensionTitles },
        methods: { setExtensionToDisplay: onClick },
    } = React.useContext(ForgeModalContext);

    const forgeActionLinkOnClick = (id: string, onClickApp: SetExtensionToDisplayType) => () => {
        onClose('navBarForgeApp');
        return onClickApp(id);
    };
    return (
        <>
            {extensionTitles.map(({ id, title }) => {
                return (
                    <CustomItem
                        id={id}
                        key={`forge-title_${id}`}
                        component={CustomButton}
                        actionSubjectId="navBarForgeApp"
                        onClick={forgeActionLinkOnClick(id, onClick)}
                    >
                        {title}
                    </CustomItem>
                );
            })}
        </>
    );
};

const logoutLink = (onLogoutClick: () => LogOutAction) => (
    <Section hasSeparator>
        <AnalyticsContext data={{ actionSubjectId: 'navbarLogout' }}>
            <CustomItem
                component={CustomButton}
                actionSubjectId="navbarLogout"
                aria-label={'logout'}
                onClick={onLogoutClick}
            >
                <FormattedMessage {...messages.logout} />
            </CustomItem>
        </AnalyticsContext>
    </Section>
);

const reportingMenuLink = (onClose: (actionSubjectId?: string) => void) => {
    return (
        <CustomItem
            actionSubjectId="navbarReporting"
            component={CustomLink}
            to="/reporting"
            onClick={() => onClose('navBarReporting')}
        >
            <FormattedMessage {...messages.reporting} />
        </CustomItem>
    );
};

export const CustomMenuLinks = ({
    customMenuLinks,
    onClose,
}: {
    customMenuLinks: CustomMenuLinkProps[];
    onClose: (actionSubjectId?: string) => void;
}) => (
    <span data-test-id="custom-menu-link">
        {customMenuLinks.map((menuLink) => (
            <div key={`custom-menu-link_${menuLink.key || ''}`} className={menuLink.styleClass}>
                <CustomItem
                    actionSubjectId={'custom-menu-link'}
                    component={CustomAnchor}
                    href={menuLink.href}
                    onClick={() => onClose('custom-menu-link')}
                    data-test-id="custom-menu-link"
                    key={`custom-menu-link-id_${menuLink.id || ''}`}
                >
                    {menuLink.label}
                </CustomItem>
            </div>
        ))}
    </span>
);

const buildCustomMenuLinks = (customMenuLinks: CustomMenuLinkProps[], onClose: (actionSubjectId?: string) => void) => {
    return customMenuLinks.map((menuLink) => (
        <div key={`build-custom-menu-link_${menuLink.key || ''}`} className={menuLink.styleClass}>
            <CustomItem
                actionSubjectId={'custom-menu-link'}
                component={CustomAnchor}
                data-test-id="custom-menu-link"
                key={`custom-menu-link-id_${menuLink.id || ''}`}
                onClick={() => onClose('custom-menu-link')}
                href={menuLink.href}
            >
                {menuLink.label}
            </CustomItem>
        </div>
    ));
};

const TopMenu = (
    myRequestCount: number,
    onClose: (actionSubjectId?: string) => void,
    pendingApprovalCount: number,
    approvalCount: number,
    isHelpCenterManagementScreen: boolean
) => {
    const customizeMenu = [
        {
            onClick: () => onClose('navbarUserRequests'),
            link: isHelpCenterManagementScreen ? '/servicedesk/customer/user/requests' : '/user/requests',
            label: <FormattedMessage {...messages.requests} />,
            actionSubjectId: 'navbarUserRequests',
            iconAfter: myRequestCount > 0 ? <AvatarBadge>{myRequestCount}</AvatarBadge> : undefined,
            isVisible: true,
        },
        {
            onClick: () => onClose('navbarUserApprovals'),
            link: isHelpCenterManagementScreen ? '/servicedesk/customer/user/approvals' : '/user/approvals',
            label: <FormattedMessage {...messages.approvals} />,
            actionSubjectId: 'navbarUserApprovals',
            iconAfter: pendingApprovalCount > 0 ? <AvatarBadge>{pendingApprovalCount}</AvatarBadge> : undefined,
            isVisible: approvalCount > 0 || pendingApprovalCount > 0,
        },
        {
            onClick: () => onClose('navbarProfile'),
            link: isHelpCenterManagementScreen ? '/servicedesk/customer/user/profile' : '/user/profile',
            label: <FormattedMessage {...messages.profile} />,
            actionSubjectId: 'navbarProfile',
            isVisible: true,
        },
    ];
    return (
        <Section>
            {customizeMenu.map((menu) => {
                if (!menu.isVisible) {
                    return null;
                }
                return (
                    <>
                        {isHelpCenterManagementScreen ? (
                            <CustomItem
                                actionSubjectId={menu.actionSubjectId}
                                key={`top-menu-management-screen_${menu.actionSubjectId}`}
                                component={CustomAnchor}
                                href={menu.link}
                                onClick={menu.onClick}
                                iconAfter={menu.iconAfter}
                            >
                                {menu.label}
                            </CustomItem>
                        ) : (
                            <CustomItem
                                key={`top-menu_${menu.actionSubjectId}`}
                                component={CustomLink}
                                to={menu.link}
                                actionSubjectId={menu.actionSubjectId}
                                onClick={menu.onClick}
                                iconAfter={menu.iconAfter}
                            >
                                {menu.label}
                            </CustomItem>
                        )}
                    </>
                );
            })}
        </Section>
    );
};

const RenderMultiHcDropdown = () => {
    const { formatMessage } = useIntl();
    return (
        <Section hasSeparator>
            <HeadingItem>{formatMessage(messages.helpCenter)}</HeadingItem>
            <Box xcss={MultiHcDropdownContainer}>
                <MultiHcDropdown />
            </Box>
        </Section>
    );
};

const CustomizeLinks = (
    onClose: (actionSubjectId?: string) => void,
    openHelpCenterCustomizationSidebar: DispatchProps['openHelpCenterCustomizationSidebar'],
    canAdministerJIRA: boolean,
    routeName: string | undefined,
    basePath: string
) => {
    di(isAdvancedCustomizationEnabled);
    const isHomePage = routeName === 'help-center' || routeName === 'view-homepage';
    const isSinglePortalPage = routeName === 'portal-home';
    const isSmallScreen = window.innerWidth < sizes.xsmall;
    const customizeMenu = [
        {
            link: `${basePath}?customize=true&experience=login-announcement`,
            label: <FormattedMessage {...messages.signIn} />,
            actionSubjectId: 'navbarLoginAnnouncement',
            isVisible: isHomePage && canAdministerJIRA && !isSmallScreen,
            onClick: () => onClick('navbarLoginAnnouncement'),
        },
        {
            link: `${basePath}?customize=true&experience=help-center-announcement`,
            label: <FormattedMessage {...messages.announcement} />,
            actionSubjectId: 'navbarAnnouncement',
            isVisible: isHomePage && !isSmallScreen,
            onClick: () => onClick('navbarAnnouncement'),
        },
        {
            link: `${basePath}?customize=true&experience=theme`,
            label: <FormattedMessage {...messages.look} />,
            actionSubjectId: 'navbarLookAndFeel',
            isVisible: (isHomePage || isSinglePortalPage) && canAdministerJIRA,
            onClick: () => onClick('navbarLookAndFeel'),
        },
        {
            link: '/settings/portal-reorder',
            label: isAdvancedCustomizationEnabled() ? (
                <FormattedMessage {...messages.topicsAndPortals} />
            ) : (
                <FormattedMessage {...messages.topic} />
            ),
            actionSubjectId: 'navbarPortalReorder',
            isVisible: isHomePage && canAdministerJIRA && !isSmallScreen,
            onClick: () => onClick('navbarPortalReorder'),
        },
        {
            link: '/edit',
            label: <FormattedMessage {...messages.hideAndReorder} />,
            actionSubjectId: 'navbarHideAndReorder',
            isVisible: isAdvancedCustomizationEnabled() && isHomePage && canAdministerJIRA && !isSmallScreen,
            onClick: () => onClick('navbarHideAndReorder'),
        },
    ];
    const onClick = (actionSubjectId: string) => {
        onClose(actionSubjectId);
        openHelpCenterCustomizationSidebar();
    };
    return (
        <Section>
            {customizeMenu.map((menu) => {
                if (!menu.isVisible) {
                    return null;
                }
                return (
                    <CustomItem
                        key={`customize-menu_${menu.actionSubjectId}`}
                        component={CustomLink}
                        to={menu.link}
                        actionSubjectId={menu.actionSubjectId}
                        onClick={menu.onClick}
                    >
                        {menu.label}
                    </CustomItem>
                );
            })}
        </Section>
    );
};

const PortalLinks = ({
    onClose,
    canCustomiseHelpCenter,
    portal,
}: {
    onClose: (actionSubjectId?: string) => void;
    canCustomiseHelpCenter: boolean | undefined;
    portal: Portal | undefined;
}) => {
    const { formatMessage } = useIntl();
    if (!portal) {
        return null;
    }
    const { isProjectSimplified, isProjectAdmin, canEditAnnouncement } = portal;
    const shouldRenderAsPortalAnnouncement = (): boolean => {
        // for simplified projects, render side bar as announcement for agents with permission
        if (isProjectSimplified && !isProjectAdmin && canEditAnnouncement) {
            return true;
        }
        // in classic projects render sidebar as announcement for users who can't modify helpcenter, but can edit announcements
        if (!isProjectSimplified && !canCustomiseHelpCenter && canEditAnnouncement) {
            return true;
        }
        return false;
    };
    const customizeMenu = [
        {
            link: '?customize=true&experience=add-announcement',
            onClick: () => onClose('navbarPortalAnnouncement'),
            label: <FormattedMessage {...messages.portalAnnouncement} />,
            actionSubjectId: 'navbarPortalAnnouncement',
            isVisible: canEditAnnouncement || shouldRenderAsPortalAnnouncement(),
        },
        {
            link: '?customize=true&experience=request-groups',
            onClick: () => onClose('navbarRequestTypeGroup'),
            label: <FormattedMessage {...messages.portalRequestTypeGroup} />,
            actionSubjectId: 'navbarRequestTypeGroup',
            isVisible: isProjectAdmin && isProjectSimplified && !shouldRenderAsPortalAnnouncement(),
        },
    ];
    const showHeading = customizeMenu.some((menu) => menu.isVisible);
    return (
        <Section>
            {showHeading && <HeadingItem>{formatMessage(messages.portal)}</HeadingItem>}
            {customizeMenu.map((menu) => {
                if (!menu.isVisible) {
                    return null;
                }
                return (
                    <CustomItem
                        key={`portal-link_${menu.actionSubjectId}`}
                        component={CustomLink}
                        to={menu.link}
                        actionSubjectId={menu.actionSubjectId}
                        onClick={menu.onClick}
                    >
                        {menu.label}
                    </CustomItem>
                );
            })}
        </Section>
    );
};

const RequestList = (
    onClose: (actionSubjectId?: string) => void,
    openRequestListCustomizationSidebar: DispatchProps['openRequestListCustomizationSidebar']
) => {
    const onClick = (actionSubjectId: string) => {
        onClose(actionSubjectId);
        openRequestListCustomizationSidebar();
    };
    const { formatMessage } = useIntl();
    return (
        <Section>
            <HeadingItem>{formatMessage(messages.requestListHeading)}</HeadingItem>
            <CustomItem
                component={CustomLink}
                to="/user/requests"
                actionSubjectId="navbarUserRequestListCustomize"
                onClick={() => onClick('navbarUserRequestListCustomize')}
            >
                <FormattedMessage {...messages.customizeRequestList} />
            </CustomItem>
        </Section>
    );
};

const UserProfileConnectActionLinks = withDeferredWrmLoaded(CustomMenuLinks, initializeConnect);

const AvatarMenuDumbV2 = (props: Props) => {
    di(ForgeModalContextProvider, ForgeActionModule, useAnalyticsEvents, getRelativeBasePath, isAdvancedHelpCenter);
    const isHelpCenterManagementScreen = getRelativeBasePath() === HELP_CENTERS_MANAGEMENT_BASE_PATH;
    const basePath = isAdvancedHelpCenter() ? '/' : '/portals';
    const [isOpen, setIsOpen] = useState(!!props.isOpen);
    const { formatMessage } = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const onItemClick = (actionSubjectId?: string) => {
        if (actionSubjectId && createAnalyticsEvent) {
            createAnalyticsEvent({
                actionSubjectId,
                action: 'clicked',
                actionSubject: 'avatarMenu',
                version: '2',
                analyticsType: UI_EVENT_TYPE,
            }).fire();
        }
        setIsOpen(false);
    };

    const onOpenChange = useCallback(
        (open: boolean) => {
            if (!!props.onOpenAndClose) {
                props.onOpenAndClose({ isOpen: open });
            }
            setIsOpen(open);
        },
        [props]
    );

    const content = () => {
        const {
            myRequestCount,
            customMenuLinks,
            onLogoutClick,
            openHelpCenterCustomizationSidebar,
            openRequestListCustomizationSidebar,
            routeName,
            pendingApprovalCount,
            approvalCount,
            canAdministerJIRA,
            canCustomiseHelpCenter,
            defaultColumnSettings,
            portal,
            jsmAIConfigEnabled,
            avatarSrc,
            displayName,
            email,
        } = props;
        const isSmallScreen = window.innerWidth < sizes.xsmall;
        return (
            <Wrapper data-testid="avatar-menu-content">
                <MenuGroup maxHeight={maxHeight} spacing={'cozy'}>
                    {<AccountDetails avatarSrc={avatarSrc} displayName={displayName} email={email} />}
                    {TopMenu(
                        myRequestCount || 0,
                        onItemClick,
                        pendingApprovalCount || 0,
                        approvalCount || 0,
                        isHelpCenterManagementScreen
                    )}
                    {!isHelpCenterManagementScreen && (
                        <>
                            {!isSmallScreen && canAdministerJIRA && <RenderMultiHcDropdown />}
                            {canCustomiseHelpCenter &&
                                CustomizeLinks(
                                    onItemClick,
                                    openHelpCenterCustomizationSidebar,
                                    canAdministerJIRA,
                                    routeName,
                                    basePath
                                )}
                            {routeName === 'portal-home' && (
                                <PortalLinks
                                    onClose={onItemClick}
                                    canCustomiseHelpCenter={canCustomiseHelpCenter}
                                    portal={portal}
                                />
                            )}
                            {routeName === 'requests-list' &&
                                canAdministerJIRA &&
                                Boolean(defaultColumnSettings?.length) &&
                                RequestList(onItemClick, openRequestListCustomizationSidebar)}
                            {isUnifiedHelpReportingPageEnabled() && jsmAIConfigEnabled && canAdministerJIRA
                                ? reportingMenuLink(onItemClick)
                                : null}
                            {customMenuLinks && customMenuLinks.length > 0 ? (
                                <Section hasSeparator={customMenuLinks && customMenuLinks.length > 0}>
                                    {isWrmOptimizationsEnabled() ? (
                                        <UserProfileConnectActionLinks
                                            customMenuLinks={customMenuLinks}
                                            onClose={onItemClick}
                                        />
                                    ) : (
                                        buildCustomMenuLinks(customMenuLinks, onItemClick)
                                    )}
                                </Section>
                            ) : undefined}
                            <ForgeTitleMenuLinks onClose={onItemClick} />
                        </>
                    )}
                    {logoutLink(onLogoutClick)}
                </MenuGroup>
            </Wrapper>
        );
    };

    const Trigger = useCallback(
        (triggerProps: Partial<TriggerProps>) => {
            const { avatarSrc, pendingApprovalCount = 0, myRequestCount = 0 } = props;
            const totalRequests = pendingApprovalCount + myRequestCount;
            return (
                <>
                    {totalRequests > 0 && <StyledAvatarBadge>{totalRequests}</StyledAvatarBadge>}
                    <div role="list">
                        <Profile
                            {...triggerProps}
                            icon={<Avatar src={avatarSrc} borderColor={token('color.border.inverse', colors.N0)} />}
                            isSelected={isOpen}
                            onClick={() => onOpenChange(!isOpen)}
                            aria-label={formatMessage(messages.tooltip)}
                            tooltip={formatMessage(messages.tooltip)}
                        />
                    </div>
                </>
            );
        },
        [formatMessage, isOpen, onOpenChange, props]
    );

    const renderDropdownMenu = () => {
        return (
            <Popup
                isOpen={isOpen}
                onClose={() => onOpenChange(false)}
                placement="bottom-end"
                zIndex={layers.modal()}
                content={content}
                trigger={Trigger}
                key={'avatar-menu-dumb-v2-popup'}
            />
        );
    };

    return (
        <AvatarMenuContainer>
            <ForgeModalContextProvider type={USER_MENU_ACTION_SECTION_MODULE}>
                {renderDropdownMenu()}
                <ForgeActionModule type={USER_MENU_ACTION_SECTION_MODULE} componentName={USER_MENU_ACTION_COMPONENT} />
            </ForgeModalContextProvider>
        </AvatarMenuContainer>
    );
};

AvatarMenuDumbV2.defaultProps = {
    customMenuLinks: [],
};

const AvatarMenuContainer = styled.div({
    paddingRight: token('space.100', '8px'),
    position: 'relative',
});

const NoUnderlineLink = styled(Link)({
    textDecoration: 'none',
    '&:focus, &:hover, &:visited, &:link, &:active': {
        textDecoration: 'none',
    },
});

const Wrapper = styled.div({
    minWidth: grid.multiple(29.25).px,
    maxWidth: grid.multiple(100).px,
    color: token('color.text', colors.N800),
});

const StyledAvatarBadge = styled(AvatarBadge)({
    position: 'absolute',
    left: 0,
    bottom: 0,
    zIndex: layers.navigation(),
});

const CustomLink = styled(NoUnderlineLink)({
    color: 'unset !important',
});

const CustomButton = styled(Button)({
    color: `${token('color.text', colors.N800)} !important`,
    '&:focus, &:hover, &:visited, &:link, &:active': {
        color: `${token('color.text', colors.N800)} !important`,
    },
});

const CustomAnchor = styled(HtmlAnchor)({
    color: 'unset !important',
    textDecoration: 'none',
    '&:focus, &:hover, &:visited, &:link, &:active': {
        textDecoration: 'none',
    },
});

const maxHeight = `calc(100vh - ${grid.multiple(24).px}`;

const MultiHcDropdownContainer = xcss({
    paddingBottom: 'space.100',
    paddingLeft: 'space.150',
    paddingRight: 'space.250',
    paddingTop: 'space.100',
});
export default AvatarMenuDumbV2;
